/**
 * CompareAggregateMethods.jsx
 *
 * Compares the probability-weighted (Primary) result with the Sampled outcomes.
 * Lives inside the Reliability tab as part of methodology / diagnostics.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { AlertCircle, GitCompareArrows } from 'lucide-react';

function DistributionTable({ label, percentages, ordered }) {
  if (!percentages) return null;
  return (
    <div className="rounded border border-slate-100 dark:border-slate-700">
      <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
        <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">{label}</p>
      </div>
      <div className="p-2 space-y-1">
        {ordered.map((opt) => (
          <div key={opt} className="flex items-center justify-between text-xs">
            <span className="text-slate-600 dark:text-slate-300 truncate">{opt}</span>
            <span className="text-slate-700 dark:text-slate-100 font-mono tabular-nums">
              {(percentages[opt] || 0).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CompareAggregateMethods({ aggregation, orderedResponseScale = [] }) {
  const [mode, setMode] = useState('expected');
  const hasExpected = !!aggregation?.expected_percentages && Object.keys(aggregation.expected_percentages).length > 0;

  if (!hasExpected) return null;

  return (
    <Card className="border border-slate-200 dark:border-border" data-testid="compare-aggregate-methods">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <GitCompareArrows className="w-4 h-4 text-slate-400" />
          Compare aggregate methods
        </CardTitle>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          Sampled outcomes are the individual simulated answers. Aggregate charts use the
          probability-weighted Primary Result. Comparison is provided for analysts.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap" data-testid="compare-methods-toggle">
          <Button
            variant={mode === 'expected' ? 'secondary' : 'outline'}
            size="sm"
            className="h-7 px-2 text-[11px]"
            onClick={() => setMode('expected')}
            data-testid="compare-methods-expected-btn"
          >
            Primary (probability-weighted)
          </Button>
          <Button
            variant={mode === 'sampled' ? 'secondary' : 'outline'}
            size="sm"
            className="h-7 px-2 text-[11px]"
            onClick={() => setMode('sampled')}
            data-testid="compare-methods-sampled-btn"
          >
            Sampled outcomes
          </Button>
          {mode === 'sampled' && (
            <span className="text-[11px] text-warning-soft-foreground ml-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Sampled outcomes may differ slightly from the Primary Result due to draw variance.
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <DistributionTable
            label="Primary (probability-weighted)"
            percentages={aggregation?.expected_percentages}
            ordered={orderedResponseScale}
          />
          <DistributionTable
            label="Sampled outcomes"
            percentages={aggregation?.percentages}
            ordered={orderedResponseScale}
          />
        </div>
      </CardContent>
    </Card>
  );
}
